import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogApi, type BlogListItem } from '../lib/blogApi';
import { SiteHeader } from '../components/SiteHeader';
import { SEO } from '../components/SEO';

/** Public blog index – list of published posts. */
export function Blog() {
  const [items, setItems] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    blogApi
      .list()
      .then((r) => setItems(r.items))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Blog — Dormant Accounts Filing"
        description="Guides and news on filing dormant company accounts with Companies House — penalties, deadlines, fee changes, and FRS 102 basics."
        path="/blog"
      />
      <SiteHeader />

      <main className="mx-auto max-w-4xl px-6 py-12 sm:py-16">
        <Link to="/" className="text-sm text-brand-700 hover:underline">
          ← Back to home
        </Link>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          The Dormant Accounts blog
        </h1>
        <p className="mt-2 max-w-2xl text-slate-600">
          Plain-English guides on filing dormant accounts, Companies House rules and what
          actually changes in UK company law.
        </p>

        {loading && <p className="mt-10 text-sm text-slate-500">Loading posts…</p>}
        {error && (
          <div className="mt-10 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
        {!loading && items.length === 0 && (
          <div className="mt-10 rounded-xl bg-slate-50 p-8 text-center">
            <p className="text-sm text-slate-600">No posts yet. Check back soon.</p>
          </div>
        )}

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {items.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group flex flex-col overflow-hidden rounded-xl bg-white ring-1 ring-slate-200 transition hover:ring-brand-300 hover:shadow-sm"
            >
              <div
                aria-hidden
                className="h-32 w-full bg-gradient-to-br from-brand-100 via-brand-50 to-white"
                style={
                  p.coverImageUrl
                    ? { backgroundImage: `url("${p.coverImageUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
                    : undefined
                }
              />
              <div className="flex flex-col gap-2 p-5">
                <div className="text-xs text-slate-500">{formatDate(p.publishedAt)}</div>
                <h2 className="text-base font-semibold text-slate-900 group-hover:text-brand-700">
                  {p.title}
                </h2>
                {p.excerpt && <p className="text-sm leading-relaxed text-slate-600">{p.excerpt}</p>}
                <div className="mt-1 text-xs font-medium text-brand-700">Read →</div>
              </div>
            </Link>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-slate-900 py-10 text-slate-400">
        <div className="mx-auto max-w-6xl px-6 text-xs">
          © {new Date().getFullYear()} Dormant Accounts Filing.{' '}
          <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{' '}
          <Link to="/terms" className="hover:text-white">Terms</Link> ·{' '}
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
      </footer>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(+d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
